





























































































































































































import { computed, defineComponent, onMounted, reactive, ref, watch } from '@vue/composition-api';
import Vue from 'vue';
import OlbIcon from '../icon/icon.vue';
import OlbInput from '../form/elements/input/Input.vue';
import VTooltipDefault, { VPopover, VClosePopover } from 'v-tooltip';
import DateUtils from './dateUtils';

Vue.component('v-popover', VPopover);
Vue.directive('close-popover', VClosePopover);
Vue.use(VTooltipDefault, { disposeTimeout: 200 });

/**
 * month: a month is displayed and allows to select a date
 * year: a year is displayed and allows to select a month
 * mutli-year: several years are displayed and allow to select one
 */
type ViewType = 'month' | 'year' | 'multi-year';
const classes = {
	selected: ['bg-primary', 'text-white'],
};

export default defineComponent({
	components: {
		OlbIcon,
		OlbInput,
	},
	props: {
		id: {
			type: String,
		},
		label: {
			type: String,
		},
		inputFormat: {
			type: String,
			required: false,
			default: DateUtils.getCurrentDateFormat(),
		},
		defaultDate: {
			type: String,
			required: false,
		},
		placeholder: {
			type: String,
			required: false,
			default: DateUtils.getCurrentDateFormat().toUpperCase(),
		},
		minDate: {
			type: String,
			required: false,
		},
		maxDate: {
			type: String,
			required: false,
		},
		disabled: {
			type: Boolean,
			required: false,
			default: false,
		},
		readonly: {
			type: Boolean,
			required: false,
			default: false,
		},
		rules: {
			type: String,
		},
		name: {
			type: String,
		},
	},
	setup(props) {
		const datepickerPopover = ref();
		const today = new Date();
		today.setUTCHours(0, 0, 0, 0);
		const state = reactive({
			selectedDate: '',
			helperDate: today,
			maxDate: null as Date | null,
			minDate: null as Date | null,
			previousDisabled: false,
			nextDisabled: false,
			hasError: false,
			selectedPeriod: {
				text: '' as string,
				type: '' as ViewType,
			},
			monthView: {
				weekdays: DateUtils.getWeekdayNames(),
				daysByWeek: [] as number[][],
				blankDays: 0,
				monthName: '',
			},
			multiYearView: {
				firstYear: 0,
				yearsByRow: [] as number[][],
			},
			yearView: {
				months: {} as Record<string, number>,
				monthsByRow: [] as string[][],
			},
			maxDateString: '',
			minDateString: '',
		});

		function setSelectedDate() {
			// resolve any error state
			state.hasError = false;
			// set selected date equal to helper date and close the popover
			state.selectedDate = DateUtils.formatDate(new Date(state.helperDate), 'yyyy-mm-dd');
			datepickerPopover.value.hide();
		}

		/**
		 * Create an array from a given stop and end point with a step of 1.
		 * E.g. if `start` equals 3 and `stop` equals 7,
		 * this function will return [3, 4, 5, 6, 7]
		 */
		function createArrayFromRange(start: number, stop: number) {
			return Array.from({ length: stop - start + 1 }, (_, i) => start + i);
		}

		/**
		 * Slice a given array into chunks of the given chunk size.
		 * E.g. if `array` equals [0, 0, 0, 1, 2, 3, 4, ... 29, 30, 31] and `chunk` equals 7,
		 * the result will be [[0, 0, 0, 1, 2, 3, 4], [5, 6, 7, 8, 9, 10, 11], ... [26, 27, 28, 29, 30, 31]].
		 */
		function createArrayOfChunks(array: any[], chunk: number) {
			let i, j;
			const result = [];
			for (i = 0, j = array.length; i < j; i += chunk) {
				// eslint-disable-next-line @typescript-eslint/ban-ts-ignore
				// @ts-ignore
				result.push(array.slice(i, i + chunk));
			}
			return result;
		}

		function spreadDaysToMonthView() {
			state.monthView.daysByWeek = [];
			/**
			 * Amount of days the selected month has.
			 * Method: get the 0th of the next month (seen from selected date).
			 * 0th = last day of previous month (in this case = selected month),
			 * which represents the amount of days of the selected month.
			 */
			const noOfDays = new Date(state.helperDate.getFullYear(), state.helperDate.getMonth() + 1, 0).getDate();
			// second: get the weekday of the 1st of this month
			/**
			 * Weekday of the selected month's first date.
			 * Must be zero based.
			 */
			let firstWeekday = new Date(state.helperDate.getFullYear(), state.helperDate.getMonth(), 1).getDay() - 1;
			// sunday = 0 --> but in our array it's at position 6!
			firstWeekday === -1 && (firstWeekday = 6);
			state.monthView.blankDays = firstWeekday;
			/**
			 * Amount of days in the week until the first (as array).
			 * E.g. if the first is a Thursday, then `blankDays` equals 3 (= Monday, Tuesday, Wednesday).
			 */
			const blankDays = new Array(firstWeekday).fill(0);
			/**
			 * Array with the days of the month. E.g. for October: [1, 2, 3, ... 30, 31]
			 */
			const filledDays = createArrayFromRange(1, noOfDays);
			const days = [...blankDays, ...filledDays];
			state.monthView.daysByWeek = createArrayOfChunks(days, 7);
			// finally, remove blank days in first week entry
			(state.monthView.daysByWeek[0] as Array<number>).splice(0, state.monthView.blankDays);
		}

		function spreadYearsToMultiYearView() {
			state.multiYearView.yearsByRow = createArrayOfChunks(
				createArrayFromRange(state.multiYearView.firstYear, state.helperDate.getFullYear()),
				4,
			);
		}

		/**
		 * Initializes min. and max. date, if they are given.
		 * Handles enabling/disabling previous and/or next button (to show the previous/next period of time).
		 */
		function handleMaxAndMinDate() {
			let date, compare;
			// initialize max. date if necessary (only if future dates are not disabled!)
			if (!state.maxDate && props.maxDate) {
				date = new Date(props.maxDate);
				compare = DateUtils.compareDates(date, new Date(state.selectedDate));
				// if the date is not null and thus can be compared to the selected one --> set the state
				if (date && compare && compare >= 0) {
					// correct state --> date is greater or equal to selected one
					state.maxDate = new Date(date);
				} else {
					state.maxDate = new Date(state.selectedDate);
				}
			}
			// initialize min. date if necessary
			if (!state.minDate && props.minDate) {
				date = new Date(props.minDate);
				compare = DateUtils.compareDates(date, new Date(state.selectedDate));
				// if the date is not null and thus can be compared to the selected one --> set the state
				if (date && compare && compare <= 0) {
					// correct state --> date is smaller or equal to selected one
					state.minDate = new Date(date);
				} else {
					state.minDate = new Date(state.selectedDate);
				}
			}

			// check previous button
			if (state.minDate) {
				switch (state.selectedPeriod.type) {
					case 'month': {
						const interimDate = DateUtils.setMonth(state.helperDate, state.helperDate.getMonth() - 1);
						state.previousDisabled = DateUtils.compareMonths(interimDate, state.minDate) < 0;
						break;
					}
					case 'year': {
						state.previousDisabled = state.helperDate.getFullYear() - 1 < state.minDate.getFullYear();
						break;
					}
					case 'multi-year': {
						state.previousDisabled = state.helperDate.getFullYear() - 24 < state.minDate.getFullYear();
						break;
					}
				}
			}

			// check next button
			if (state.maxDate) {
				switch (state.selectedPeriod.type) {
					case 'month': {
						const interimDate = DateUtils.setMonth(state.helperDate, state.helperDate.getMonth() + 1);
						state.nextDisabled = DateUtils.compareMonths(interimDate, state.maxDate) > 0;
						break;
					}
					case 'year': {
						state.nextDisabled = state.helperDate.getFullYear() + 1 > state.maxDate.getFullYear();
						break;
					}
					case 'multi-year': {
						state.nextDisabled = state.helperDate.getFullYear() >= state.maxDate.getFullYear();
						break;
					}
				}
			}
		}

		function setPeriod(viewType?: ViewType) {
			viewType && (state.selectedPeriod.type = viewType);
			// adjust the selected period's text depending on the type of the selection range
			switch (state.selectedPeriod.type) {
				case 'month':
					state.monthView.monthName = DateUtils.getMonthName(state.helperDate.getMonth());
					// e.g. shown text = OKT. 2020
					state.selectedPeriod.text = state.monthView.monthName + ' ' + state.helperDate.getFullYear();
					spreadDaysToMonthView();
					break;
				case 'multi-year':
					// get the last 24 years (= 4 columns x 6 rows)
					// (it's -23 since we have to count the current one as well)
					state.multiYearView.firstYear = state.helperDate.getFullYear() - 23;
					state.selectedPeriod.text = state.multiYearView.firstYear + ' - ' + state.helperDate.getFullYear();
					spreadYearsToMultiYearView();
					break;
				case 'year':
					state.selectedPeriod.text = state.helperDate.getFullYear() + '';
					// no need to spread the months to the year view,
					//this is done during initialization since it will always look the same
					break;
			}
			handleMaxAndMinDate();
		}

		function initYearView() {
			const monthNames = DateUtils.getMonthNames();
			// create an object {'JAN.': 0, 'FEB.': 1, ... 'DEZ.': 11} (months are 0-based in Date)
			state.yearView.months = createArrayFromRange(0, 11).reduce(function(result, field, index) {
				// eslint-disable-next-line @typescript-eslint/ban-ts-ignore
				// @ts-ignore
				result[monthNames[index]] = field;
				return result;
			}, {});
			state.yearView.monthsByRow = createArrayOfChunks(monthNames, 4);
		}

		function setInitialSelectedDate() {
			if (props.defaultDate) {
				const initialDate = new Date(props.defaultDate);
				if (initialDate) {
					state.hasError = false;
					state.helperDate = new Date(initialDate);
					state.selectedDate = DateUtils.formatDate(new Date(initialDate), 'yyyy-mm-dd');
				}
			}
			initYearView();
		}

		/**
		 * Change views when clicking on the period information. Order is
		 * * Current view is `month` --> Switch to `multi-year` view
		 * * Current view is `year` --> Switch to `month` view
		 * * Current view is `multi-year` --> Switch to `month` view
		 */
		function toggleView() {
			switch (state.selectedPeriod.type) {
				case 'month':
					// always display the current year as "starting point" for multi-year view
					state.helperDate.setFullYear(today.getFullYear());
					setPeriod('multi-year');
					break;
				case 'multi-year':
				case 'year':
					setPeriod('month');
					break;
			}
		}

		function selectPreviousPeriod() {
			if (state.previousDisabled) return;
			switch (state.selectedPeriod.type) {
				case 'month':
					state.helperDate = DateUtils.setMonth(state.helperDate, state.helperDate.getMonth() - 1);
					break;
				case 'year':
					state.helperDate.setFullYear(state.helperDate.getFullYear() - 1);
					break;
				case 'multi-year':
					state.helperDate.setFullYear(state.helperDate.getFullYear() - 24);
					break;
			}
			setPeriod();
		}

		function selectNextPeriod() {
			if (state.nextDisabled) return;
			switch (state.selectedPeriod.type) {
				case 'month':
					state.helperDate = DateUtils.setMonth(state.helperDate, state.helperDate.getMonth() + 1);
					break;
				case 'year':
					state.helperDate.setFullYear(state.helperDate.getFullYear() + 1);
					break;
				case 'multi-year':
					state.helperDate.setFullYear(state.helperDate.getFullYear() + 24);
					break;
			}
			setPeriod();
		}

		/**
		 * Returns some classes if the respective element's value has points of contact with
		 * * the current date (= today)
		 * * the selected date
		 * * minimum date
		 * * maximum date
		 *
		 * The classes differ, depending on the element's type (cell or wrapper).
		 */
		function setClasses(elementType: 'cell' | 'wrapper', value: string) {
			const appliedClasses = [];
			let date = new Date(state.helperDate);
			const month = state.yearView.months[value];

			// all cases (= all period type checks) are build the same:
			// 1. update `date`'s value
			// 2. check for convergence with today's date and the selected date
			// 3. check that the value doesn't exceed min. or max. date
			switch (state.selectedPeriod.type) {
				case 'multi-year':
					// set year
					date.setFullYear(parseInt(value));
					if (elementType === 'cell') {
						// check convergence with today and selected date
						date.getFullYear() === today.getFullYear() && appliedClasses.push('olb-datepicker-today');
						date.getFullYear() === new Date(state.selectedDate).getFullYear() &&
							appliedClasses.push(...classes.selected);
					} else if (elementType === 'wrapper') {
						// check min. and max. date
						((state.maxDate && date.getFullYear() > state.maxDate.getFullYear()) ||
							(state.minDate && date.getFullYear() < state.minDate.getFullYear())) &&
							appliedClasses.push('olb-datepicker-disabled');
					}
					break;
				case 'year':
					// January (0) is a special case because it is interpreted as null,
					// so we have to set it explicitly!
					if (month === 0) {
						date.setMonth(0);
					} else {
						date = DateUtils.setMonth(date, month);
					}

					if (elementType === 'cell') {
						// check convergence with today and selected date
						DateUtils.compareMonths(date, today) === 0 && appliedClasses.push('olb-datepicker-today');
						DateUtils.compareMonths(date, new Date(state.selectedDate)) === 0 &&
							appliedClasses.push(...classes.selected);
					} else if (elementType === 'wrapper') {
						// check min. and max. date
						((state.maxDate && DateUtils.compareMonths(date, state.maxDate) > 0) ||
							(state.minDate && DateUtils.compareMonths(date, state.minDate) < 0)) &&
							appliedClasses.push('olb-datepicker-disabled');
					}
					break;
				case 'month':
					// set day
					date.setDate(parseInt(value));
					if (elementType === 'cell') {
						// check convergence with today and selected date
						DateUtils.compareDates(date, today) === 0 && appliedClasses.push('olb-datepicker-today');
						DateUtils.compareDates(date, new Date(state.selectedDate)) === 0 &&
							appliedClasses.push(...classes.selected);
					} else if (elementType === 'wrapper') {
						// check min. and max. date
						const compareToMax = DateUtils.compareDates(date, state.maxDate);
						const compareToMin = DateUtils.compareDates(date, state.minDate);
						((compareToMax && compareToMax > 0) || (compareToMin && compareToMin < 0)) &&
							appliedClasses.push('olb-datepicker-disabled');
					}
					break;
			}
			return appliedClasses.join(' ');
		}

		/**
		 * Always set the helper date back to the selected one when the datepicker popover closes.
		 */
		function datepickerCloseHandler() {
			if (!state.selectedDate) return;
			state.helperDate = new Date(state.selectedDate);
		}

		/**
		 * Handle a date, that was manually inserted. (On blur and when the user clicks enter.)
		 * First, parse it, then - if it's a valid date and thus not null - set selected date and helper date accordingly
		 * and update the view period.
		 */
		function datepickerInputHandler(event: Event) {
			const inputEl = event.target as HTMLInputElement;
			const value = inputEl.value;
			if (value === '') {
				return;
			}
			// parse the entered date
			const parsedDate = DateUtils.parseDate(inputEl.value, 'yyyy-mm-dd');
			if (parsedDate !== null) {
				// check that the entered date doesn't exceed min. or max. date
				const compareMin = state.minDate && DateUtils.compareDates(parsedDate, state.minDate);
				const compareMax = state.maxDate && DateUtils.compareDates(parsedDate, state.maxDate);
				if ((compareMin && compareMin < 0) || (compareMax && compareMax > 0)) {
					state.hasError = true;
					return;
				}
				state.hasError = false;
				state.selectedDate = DateUtils.formatDate(new Date(parsedDate), 'yyyy-mm-dd');
				state.helperDate = new Date(parsedDate);
				setPeriod();
			} else {
				state.hasError = true;
			}
		}

		onMounted(() => {
			setInitialSelectedDate();
		});

		watch(state, () => {
			state.minDateString = state.minDate ? DateUtils.formatDate(state.minDate, 'yyyy-mm-dd') : '';
			state.maxDateString = state.maxDate ? DateUtils.formatDate(state.maxDate, 'yyyy-mm-dd') : '';
		});

		/**
		 * Checks, whether the clicked cell is disabled or not based on the classes it's wrapper and the cell itself has:
		 * When the cell is supposed to be disabled (e.g. because it's value exceeds min. or max. date),
		 * the logic checks, whether it's wrapper has the class `olb-datepicker-disabled`.
		 */
		function isDisabledCell(event: Event) {
			const clickedEl = event.target as HTMLElement;
			const clickedElClasses = clickedEl.classList;
			const wrapperElClasses = clickedEl.parentElement?.classList;
			return (
				clickedElClasses?.contains('olb-datepicker-disabled') ||
				wrapperElClasses?.contains('olb-datepicker-disabled')
			);
		}

		function setMonthInYearView(event: MouseEvent) {
			const text = (event.target as HTMLElement).textContent;
			if (text !== null) {
				state.helperDate = DateUtils.setMonth(state.helperDate, state.yearView.months[text.trim()]);
				setPeriod('month');
			}
		}

		return {
			state,
			selectPreviousPeriod,
			selectNextPeriod,
			toggleView,
			setSelectedDate,
			setPeriod,
			setClasses,
			datepickerCloseHandler,
			datepickerInputHandler,
			datepickerPopover,
			isDisabledCell,
			setMonthInYearView,
		};
	},
});
